import React, { useContext, useEffect, useState } from 'react';
import { Web3Context } from '../../context/Web3Context';
import useContract from '../../hooks/useContract';
import { formatUSDT } from '../../utils/formatters';
import { PACKAGE_NAMES, PACKAGE_PRICES } from '../../utils/constants';
import Loader from '../../components/Loader/Loader';
import './Packages.css';

const PACKAGE_TIERS = ['#00E5FF', '#7B61FF', '#E5E4E2', '#FFD700', '#B9F2FF', '#FF2D95'];

const Packages = () => {
  const { userId } = useContext(Web3Context);
  const { getUserPackages, getUserPackageDetails, upgradePackage, retopupPackage, loading } = useContract();
  const [packages, setPackages] = useState([]);
  const [details, setDetails] = useState({});
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setPageLoading(true);
      try {
        const pkgs = await getUserPackages();
        setPackages(pkgs);
        const dets = {};
        for (let i = 1; i <= 6; i++) {
          if (pkgs[i - 1]) {
            dets[i] = await getUserPackageDetails(null, i);
          }
        }
        setDetails(dets);
      } catch (err) {
        console.error(err);
      } finally {
        setPageLoading(false);
      }
    };
    fetchData();
  }, [userId]);

  const handleUpgrade = async (pkgId) => {
    try {
      await upgradePackage(pkgId);
      const pkgs = await getUserPackages();
      setPackages(pkgs);
      const det = await getUserPackageDetails(null, pkgId);
      setDetails((prev) => ({ ...prev, [pkgId]: det }));
    } catch {}
  };

  const handleRetopup = async (pkgId) => {
    try {
      await retopupPackage(pkgId);
      const det = await getUserPackageDetails(null, pkgId);
      setDetails((prev) => ({ ...prev, [pkgId]: det }));
    } catch {}
  };

  if (pageLoading) return <Loader text="Loading Packages" />;

  return (
    <div className="packages-page">
      <div className="page-header">
        <h1 className="page-title">Packages</h1>
        <p className="page-subtitle">Activate and manage your investment packages</p>
      </div>

      <div className="packages-list">
        {PACKAGE_NAMES.map((name, i) => {
          const pkgId = i + 1;
          const isActive = packages[i];
          const detail = details[pkgId];
          const canUpgrade = !isActive && (pkgId === 1 || packages[i - 1]);
          const tierColor = PACKAGE_TIERS[i];

          return (
            <div key={pkgId} className={`package-card ${isActive ? 'active' : ''}`} style={{ '--tier-color': tierColor }}>
              <div className="pkg-tier-line"></div>
              <div className="pkg-header">
                <div className="pkg-rank">{String(pkgId).padStart(2, '0')}</div>
                <div className="pkg-name-section">
                  <h3 className="pkg-name">{name}</h3>
                  <span className={`pkg-status-badge ${isActive ? 'active' : ''}`}>
                    {isActive ? 'Active' : 'Locked'}
                  </span>
                </div>
                <div className="pkg-price">${PACKAGE_PRICES[i]}<span className="pkg-currency">USDT</span></div>
              </div>

              {isActive && detail ? (
                <div className="pkg-details">
                  <div className="pkg-detail-row">
                    <span>Total Invested</span>
                    <span>{formatUSDT(detail.totalInvested)} USDT</span>
                  </div>
                  <div className="pkg-detail-row">
                    <span>Total Earned</span>
                    <span className="earned">{formatUSDT(detail.totalEarned)} USDT</span>
                  </div>
                  <div className="pkg-detail-row">
                    <span>Ceiling Limit</span>
                    <span>{formatUSDT(detail.ceilingLimit)} USDT</span>
                  </div>
                  <div className="pkg-detail-row">
                    <span>Remaining</span>
                    <span className="remaining">{formatUSDT(detail.remainingLimit)} USDT</span>
                  </div>
                  <div className="pkg-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${detail.ceilingLimit > 0 ? (parseFloat(detail.totalEarned) / parseFloat(detail.ceilingLimit)) * 100 : 0}%`,
                        }}
                      ></div>
                    </div>
                    <span className="progress-text">
                      {detail.ceilingLimit > 0
                        ? ((parseFloat(detail.totalEarned) / parseFloat(detail.ceilingLimit)) * 100).toFixed(1)
                        : 0}%
                    </span>
                  </div>
                  <button className="btn-retopup" onClick={() => handleRetopup(pkgId)} disabled={loading}>
                    Re-Topup
                  </button>
                </div>
              ) : canUpgrade ? (
                <div className="pkg-details">
                  <p className="pkg-desc">Activate this package to unlock matrix level income and pool eligibility.</p>
                  <button className="btn-primary btn-full" onClick={() => handleUpgrade(pkgId)} disabled={loading}>
                    {loading ? 'Processing...' : `Activate for $${PACKAGE_PRICES[i]}`}
                  </button>
                </div>
              ) : !isActive ? (
                <div className="pkg-details">
                  <p className="pkg-locked-msg">Activate the previous package first</p>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Packages;
